import React, { useState } from 'react';
import { differenceInMinutes } from 'date-fns';
import moment from 'moment';
import { Colors } from 'library';
import { Alert, BackLink, Card, Icon, Modal, ModalFooter, PillButton, Row, Stack, Text, Title } from 'library/ui';
import { Check, ChevronRight, Close, Create, PriorityHigh } from '@mui/icons-material';
import { format12HourTime, formatShortWeekdayMonthDay } from 'shared/Dates';
import { approveReport, getBreakEnd, getBreakStart } from 'parent-portal/subs/HourReportMethods';
import { ChangeHours } from 'parent-portal/subs/ConfirmSubHours/ChangeModal';
import { markNotWorked } from 'api/HourReportApi';

import type { IHourReport } from 'internal/payments/HourReportModels';

type Step = 'options' | 'approve' | 'adjust' | 'noshow';

export function TimesheetOptionsModal({
    isOpen,
    onClose,
    onComplete,
    report,
}: {
    isOpen: boolean;
    onClose: () => void;
    onComplete: () => void;
    report: IHourReport;
}) {
    const [step, setStep] = useState<Step>('options');
    const [loading, setLoading] = useState(false);

    const firstName = report.provider.user.first_name;

    const breakStart = getBreakStart(report);
    const breakEnd = getBreakEnd(report);
    const breakMinutes = breakStart && breakEnd ? differenceInMinutes(new Date(breakEnd), new Date(breakStart)) : null;

    function finish() {
        onComplete();
        onClose();
    }

    function onApprove() {
        setLoading(true);
        approveReport(report.id, { business_approved_date: moment() })
            .catch(() => undefined)
            .finally(finish);
    }

    function onConfirmNoShow() {
        setLoading(true);
        markNotWorked(report.id)
            .catch(() => undefined)
            .finally(finish);
    }

    if (!isOpen) return null;

    const titles: Record<Step, string> = {
        options: 'Timesheet Options',
        approve: 'Approve these times',
        adjust: `Change ${firstName}'s hours · ${formatShortWeekdayMonthDay(report.start ?? new Date())}`,
        noshow: `Did ${firstName} not work their shift?`,
    };

    return (
        <Modal onClose={onClose}>
            <Stack gap="none">
                {step !== 'options' && <BackLink label="Back" onClick={() => setStep('options')} />}
                <Title size="h2">{titles[step]}</Title>
            </Stack>

            {(step === 'options' || step === 'approve') && <SummaryCard report={report} breakMinutes={breakMinutes} />}

            {step === 'options' && (
                <Stack gap="sm">
                    <OptionCard
                        title="Approve these times"
                        subtitle="Confirm the recorded hours and pay this shift"
                        icon={Check}
                        iconBg={Colors.secondary}
                        iconColor={Colors.white}
                        tone="success"
                        onClick={() => setStep('approve')}
                    />
                    <OptionCard
                        title="Adjust the times"
                        subtitle="Edit clock-in, clock-out, or add a break"
                        icon={Create}
                        iconBg={Colors.fill}
                        iconColor={Colors.textSecondary}
                        onClick={() => setStep('adjust')}
                    />
                    <OptionCard
                        title="Mark as no-show"
                        subtitle="Report that the worker didn't work this shift"
                        icon={Close}
                        iconBg={Colors.errorBackground}
                        iconColor={Colors.error}
                        onClick={() => setStep('noshow')}
                    />
                </Stack>
            )}

            {step === 'approve' && (
                <>
                    <Text tone="secondary" lineHeight="relaxed">
                        This approves <Text weight="bold">{firstName}&rsquo;s</Text> hours exactly as recorded above.
                        They&rsquo;ll be paid for this shift and the timesheet will be locked.
                    </Text>
                    <ModalFooter>
                        <PillButton variant="secondary" onClick={() => setStep('options')}>
                            Cancel
                        </PillButton>
                        <PillButton disabled={loading} onClick={onApprove}>
                            {loading ? 'Approving...' : 'Yes, approve these hours'}
                        </PillButton>
                    </ModalFooter>
                </>
            )}

            {step === 'adjust' && <ChangeHours report={report} onCancel={() => setStep('options')} onDone={finish} />}

            {step === 'noshow' && (
                <>
                    <Text tone="secondary" lineHeight="relaxed">
                        We&rsquo;re sorry for the inconvenience &mdash; we take no-shows seriously. Before you continue,
                        please review what this means.
                    </Text>
                    <Alert variant="warning">
                        <Row gap="sm" align="start">
                            <Icon icon={PriorityHigh} size="sm" iconColor={Colors.white} bgColor={Colors.warning} />
                            <Stack gap="sm">
                                <Text weight="bold">This has consequences for the worker</Text>
                                <Text size="sm" tone="secondary" lineHeight="relaxed">
                                    Reporting a no-show deducts <Text weight="bold">75 reliability points</Text> from{' '}
                                    {firstName} and may be grounds for removal from the platform. Only confirm if they
                                    genuinely did not work this shift.
                                </Text>
                            </Stack>
                        </Row>
                    </Alert>
                    <ModalFooter>
                        <PillButton variant="secondary" onClick={() => setStep('options')}>
                            Go back
                        </PillButton>
                        <PillButton disabled={loading} onClick={onConfirmNoShow}>
                            {loading ? 'Confirming...' : 'Confirm no-show'}
                        </PillButton>
                    </ModalFooter>
                </>
            )}
        </Modal>
    );
}

function SummaryCard({ report, breakMinutes }: { report: IHourReport; breakMinutes: number | null }) {
    return (
        <Card tone="muted">
            <Row justify="between" align="start">
                <SummaryItem label="Clock In" value={format12HourTime(report.start, report.timezone)} />
                <SummaryItem label="Clock Out" value={format12HourTime(report.end, report.timezone)} />
                <SummaryItem label="Break" value={breakMinutes ? `${breakMinutes} min` : 'None taken'} />
            </Row>
        </Card>
    );
}

function SummaryItem({ label, value }: { label: string; value: string }) {
    return (
        <Stack gap="none">
            <Text size="xs" weight="semibold" tone="secondary" uppercase>
                {label}
            </Text>
            <Text size="lg" weight="bold">
                {value}
            </Text>
        </Stack>
    );
}

function OptionCard({
    title,
    subtitle,
    icon,
    iconBg,
    iconColor,
    tone,
    onClick,
}: {
    title: string;
    subtitle: string;
    icon: typeof Check;
    iconBg: string;
    iconColor: string;
    tone?: 'success';
    onClick: () => void;
}) {
    return (
        <Card onClick={onClick} tone={tone} padding="sm">
            <Row gap="md" align="center">
                <Icon icon={icon} size="md" iconColor={iconColor} bgColor={iconBg} />
                <Stack gap="none" grow>
                    <Text weight="bold">{title}</Text>
                    <Text size="sm" tone="secondary">
                        {subtitle}
                    </Text>
                </Stack>
                <ChevronRight sx={{ fontSize: 22, color: Colors.textSecondary }} />
            </Row>
        </Card>
    );
}
